// Tufting plan: stroke geometry for the scroll-driven rug animation.

export interface Dot {
  t: number;
  x: number;
  y: number;
  r: number;
  c: string;
}

export interface TuftStroke {
  color: string;
  dark: string;
  light: string;
  width: number;
  pts: { x: number; y: number }[];
  cum: number[];
  len: number;
  dots: Dot[];
}

export interface TuftPlan {
  strokes: TuftStroke[];
  totalLen: number;
  fuzz: { x: number; y: number; r: number; c: string }[];
}

function mulberry32(seed: number) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function shade(hex: string, amt: number): string {
  const [r, g, b] = hexToRgb(hex);
  const f = (c: number) => Math.round(amt > 0 ? c + (255 - c) * amt : c * (1 + amt));
  return `rgb(${f(r)},${f(g)},${f(b)})`;
}

function dist(a: { x: number; y: number }, b: { x: number; y: number }) {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function buildStroke(color: string, width: number, pts: { x: number; y: number }[], rnd: () => number): TuftStroke {
  const cum = [0];
  for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + dist(pts[i - 1], pts[i]));
  const len = cum[cum.length - 1];
  const dark = shade(color, -0.22);
  const light = shade(color, 0.3);
  const dots: Dot[] = [];
  const step = 7;
  for (let t = 2; t < len; t += step) {
    const p = pointAt(pts, cum, t);
    const p2 = pointAt(pts, cum, Math.min(t + 1, len));
    const ang = Math.atan2(p2.y - p.y, p2.x - p.x) + Math.PI / 2;
    const off = (rnd() - 0.5) * width * 0.62;
    dots.push({
      t,
      x: p.x + Math.cos(ang) * off,
      y: p.y + Math.sin(ang) * off,
      r: 0.9 + rnd() * 1.5,
      c: rnd() < 0.5 ? dark : light,
    });
  }
  return { color, dark, light, width, pts, cum, len, dots };
}

function pointAt(pts: { x: number; y: number }[], cum: number[], target: number) {
  const n = cum.length;
  if (target <= 0) return pts[0];
  if (target >= cum[n - 1]) return pts[n - 1];
  let lo = 0;
  let hi = n - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (cum[mid] <= target) lo = mid;
    else hi = mid;
  }
  const seg = cum[hi] - cum[lo] || 1;
  const f = (target - cum[lo]) / seg;
  return { x: pts[lo].x + (pts[hi].x - pts[lo].x) * f, y: pts[lo].y + (pts[hi].y - pts[lo].y) * f };
}

function strokePointAt(s: TuftStroke, target: number) {
  return pointAt(s.pts, s.cum, target);
}

function serpentine(x0: number, y0: number, x1: number, y1: number, rows: number) {
  const pts: { x: number; y: number }[] = [];
  const dy = (y1 - y0) / rows;
  for (let i = 0; i <= rows; i++) {
    const y = y0 + i * dy;
    const leftToRight = i % 2 === 0;
    pts.push({ x: leftToRight ? x0 : x1, y });
    pts.push({ x: leftToRight ? x1 : x0, y });
  }
  return pts;
}

function roundedRectPath(x0: number, y0: number, x1: number, y1: number, r: number, seg = 8) {
  const pts: { x: number; y: number }[] = [];
  const corner = (cx: number, cy: number, a0: number) => {
    for (let i = 0; i <= seg; i++) {
      const a = a0 + (Math.PI / 2) * (i / seg);
      pts.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
    }
  };
  corner(x1 - r, y0 + r, -Math.PI / 2);
  corner(x1 - r, y1 - r, 0);
  corner(x0 + r, y1 - r, Math.PI / 2);
  corner(x0 + r, y0 + r, Math.PI);
  return pts;
}

function ellipseLoop(cx: number, cy: number, rx: number, ry: number, rot: number, seg = 40) {
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i <= seg; i++) {
    const t = (i / seg) * Math.PI * 2;
    const x = rx * Math.cos(t);
    const y = ry * Math.sin(t);
    pts.push({
      x: cx + x * Math.cos(rot) - y * Math.sin(rot),
      y: cy + x * Math.sin(rot) + y * Math.cos(rot),
    });
  }
  return pts;
}

function spiralPts(cx: number, cy: number, r0: number, r1: number, turns: number, seg = 140) {
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i <= seg; i++) {
    const f = i / seg;
    const a = f * turns * Math.PI * 2;
    const r = r0 + (r1 - r0) * f;
    pts.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
  }
  return pts;
}

function leafPath(ax: number, ay: number, bx: number, by: number, bulge: number) {
  const pts: { x: number; y: number }[] = [];
  const mx = (ax + bx) / 2;
  const my = (ay + by) / 2;
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const c1 = { x: mx + nx * bulge, y: my + ny * bulge };
  const c2 = { x: mx - nx * bulge, y: my - ny * bulge };
  for (let i = 0; i <= 24; i++) {
    const t = i / 24;
    const u = 1 - t;
    pts.push({
      x: u * u * ax + 2 * u * t * c1.x + t * t * bx,
      y: u * u * ay + 2 * u * t * c1.y + t * t * by,
    });
  }
  for (let i = 0; i <= 24; i++) {
    const t = i / 24;
    const u = 1 - t;
    pts.push({
      x: u * u * bx + 2 * u * t * c2.x + t * t * ax,
      y: u * u * by + 2 * u * t * c2.y + t * t * ay,
    });
  }
  return pts;
}

export function buildTuftPlan(): TuftPlan {
  const rnd = mulberry32(20260201);
  const FILL = "#E5D7B4";
  const FOREST = "#183E35";
  const BURGUNDY = "#742F40";
  const GOLD = "#B79A61";

  const strokes: TuftStroke[] = [];

  // 1. Ivory field (serpentine rows)
  strokes.push(buildStroke(FILL, 30, serpentine(122, 120, 878, 580, 24), rnd));
  // 2. Forest border
  strokes.push(buildStroke(FOREST, 22, roundedRectPath(142, 140, 858, 560, 30), rnd));
  // 3. Six petals around the medallion
  for (let i = 0; i < 6; i++) {
    const a = ((i * 60 - 90) * Math.PI) / 180;
    const cx = 500 + Math.cos(a) * 158;
    const cy = 350 + Math.sin(a) * 158;
    strokes.push(buildStroke(FOREST, 24, ellipseLoop(cx, cy, 58, 32, a + Math.PI / 2), rnd));
  }
  // 4. Burgundy centre spiral
  strokes.push(buildStroke(BURGUNDY, 26, spiralPts(500, 350, 12, 84, 5.2), rnd));
  // 5. Brass leaves on the diagonals
  for (let i = 0; i < 4; i++) {
    const a = (((45 + i * 90) * Math.PI) / 180);
    strokes.push(
      buildStroke(
        GOLD,
        16,
        leafPath(
          500 + Math.cos(a) * 300,
          350 + Math.sin(a) * 245,
          500 + Math.cos(a) * 168,
          350 + Math.sin(a) * 148,
          34
        ),
        rnd
      )
    );
  }
  // 6. Corner dots
  const dotsAt: Array<[number, number, string, number]> = [
    [196, 186, GOLD, 13],
    [804, 186, GOLD, 13],
    [196, 514, BURGUNDY, 13],
    [804, 514, BURGUNDY, 13],
  ];
  for (const [cx, cy, color, r] of dotsAt) {
    strokes.push(buildStroke(color, 12, ellipseLoop(cx, cy, r, r, 0, 24), rnd));
  }

  const totalLen = strokes.reduce((s, st) => s + st.len, 0);

  const fuzz: { x: number; y: number; r: number; c: string }[] = [];
  const palette = [FILL, FOREST, BURGUNDY, GOLD];
  for (let i = 0; i < 520; i++) {
    const color = palette[Math.floor(rnd() * palette.length)];
    fuzz.push({
      x: 120 + rnd() * 760,
      y: 122 + rnd() * 456,
      r: 0.8 + rnd() * 1.6,
      c: rnd() < 0.5 ? shade(color, -0.25) : shade(color, 0.35),
    });
  }

  return { strokes, totalLen, fuzz };
}

export interface PlanPoint {
  x: number;
  y: number;
  angle: number;
  strokeIdx: number;
}

export function planPointAtLen(plan: TuftPlan, target: number): PlanPoint | null {
  let remaining = target;
  for (let i = 0; i < plan.strokes.length; i++) {
    const s = plan.strokes[i];
    if (remaining <= s.len) {
      const p = strokePointAt(s, remaining);
      const p2 = strokePointAt(s, Math.min(remaining + 4, s.len));
      return { x: p.x, y: p.y, angle: Math.atan2(p2.y - p.y, p2.x - p.x), strokeIdx: i };
    }
    remaining -= s.len;
  }
  return null;
}
